import pdfMake from 'pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import handleHospital from "@/services/modules/hospital";
import useDate from '@/services/utils/day';

pdfMake.fonts = {
    SulaimanLipi: {
        normal: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
        bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
        italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
        bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf'
    }
}

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const opdPrescriptionPdf = () => {
    const { formatDate, formatToDdMmYy } = useDate();
    const { calculateAge } = handleHospital();

    const exportToPDF = (company, invoice, qrcode, userName, isHideHeader, isHideFooter) => {
        const serviceResource = getServiceResource(invoice);

        const doc = {
            pageSize: 'A4',
            pageMargins: getPageMargins(invoice),
            header: isHideHeader ? {} : getHeader(company, serviceResource),
            footer: isHideFooter ? {} : getFooter(userName, qrcode, serviceResource),
            content: getContent(invoice),
            defaultStyle: {
                color: 'black',
                fontSize: 10,
                font: 'SulaimanLipi'
            },
            info: {
                title: 'OPD Prescription'
            }
        }

        pdfMake.createPdf(doc).open();
    }

    const getPageMargins = (invoice) => {
        const serviceResource = getServiceResource(invoice);

        if (! serviceResource) {
            return [25, 170, 25, 86];
        }

        return [
            serviceResource.left_margin || 25,
            serviceResource.top_margin || 170,
            serviceResource.right_margin || 25,
            serviceResource.bottom_margin || 86
        ]
    }

    const getServiceResource = (invoice) => {
        if (invoice.service_general && invoice.service_general.service_resource) return invoice.service_general.service_resource;
        if (invoice.service_resource) return invoice.service_resource;
        return null;
    }

    const getPhotoBanner = (image) => {
        return {
            columns: [
                {
                    image: image,
                    width: 595.28,
                    height: 120,
                    alignment: 'center'
                }
            ]
        }
    }

    const getHeader = (company, serviceResource) => {
        if (serviceResource && serviceResource.cover_photo64) return getPhotoBanner(serviceResource.cover_photo64);

        return {
            margin: [25, 25, 25, 10],
            columns: [
                company.logo64
                    ? {
                        alignment: 'left',
                        image: company.logo64,
                        maxHeight: 60
                    }
                    : null,
                {
                    alignment: 'right',
                    stack: [
                        company.name ? company.name : '',
                        company.address ? 'Address: ' + company.address : '',
                        company.phone ? 'Phone: ' + company.phone : '',
                        company.email ? 'E-mail: ' + company.email : '',
                        company.vat_no ? 'VAT Reg No: ' + company.vat_no : ''
                    ]
                }
            ].filter(Boolean)
        }
    }

    const getFooter = (userName, qrcode, serviceResource) => {
        if (serviceResource && serviceResource.footer_photo64) return getPhotoBanner(serviceResource.footer_photo64);

        return {
            margin: [25, 0],
            columns: [
                {
                    columns: [
                        { svg: qrcode },
                        {
                            width: '90%',
                            alignment: 'left',
                            text: 'N.B: This is a system generated documents and requires no manual signature.',
                            margin: [5, 53, 0, 0],
                            fontSize: 8
                        }
                    ]
                },
                {
                    width: '20%',
                    alignment: 'right',
                    fontSize: 8,
                    margin: [0, 33, 0, 0],
                    stack: [
                        { text: 'Printed by', bold: true },
                        { text: userName },
                        { text: formatDate(new Date()) }
                    ]
                }
            ]
        }
    }

    const getContent = (invoice) => {
        return [
            getInfoTable(invoice),
            getPaymentRow(invoice),
            getPrescriptionBody()
        ]
    }

    const getPrescriptionBody = () => {
        return {
            margin: [0, 10, 0, 0],
            table: {
                widths: ['32%', '68%'],
                heights: [480],
                body: [[
                    {
                        stack: getLeftSections(),
                        border: [false, false, true, false]
                    },
                    {
                        stack: getRxSection(),
                        border: [false, false, false, false]
                    }
                ]]
            },
            layout: {
                hLineWidth: function () {
                    return 0;
                },
                vLineWidth: function () {
                    return 1;
                },
                paddingLeft: function (i) {
                    return i === 0 ? 0 : 12;
                },
                paddingTop: function () {
                    return 0;
                }
            }
        }
    }

    const getLeftSections = () => {
        const sections = ['C/C', 'O/E', 'Investigation', 'Diagnosis', 'Advice'];
        const stack = [];

        sections.forEach(function (name) {
            stack.push({
                text: name,
                fontSize: 9,
                margin: [0, 0, 0, 55]
            })
        })

        return stack;
    }

    const getRxSection = () => {
        return [{
            text: 'Rx',
            fontSize: 16,
            margin: [0, 0, 0, 10]
        }]
    }

    const getInfoTable = (invoice) => {
        return {
            margin: [0, 10, 0, 0],
            table: {
                widths: ['*', '*', '*'],
                body: [
                    [
                        getLabelValue('Bill No. :', getBillNumber(invoice)),
                        getLabelValue('Bill Date:', getBillDate(invoice)),
                        getLabelValue('Printed on:', formatDate(new Date()))
                    ],
                    [
                        getLabelValue('Name :', getPatientName(invoice)),
                        getLabelValue('Age :', getPatientAge(invoice)),
                        getLabelValue('Sex :', getPatientGender(invoice))
                    ],
                    [
                        getLabelValue('Patient ID :', getPatientId(invoice)),
                        getLabelValue('Mobile :', getPatientMobile(invoice)),
                        ''
                    ],
                    [
                        Object.assign({ colSpan: 3 }, getLabelValue('DOCTOR :', getDoctorName(invoice))),
                        '',
                        ''
                    ]
                ]
            },
            layout: 'noBorders'
        }
    }

    const getPaymentRow = (invoice) => {
        return {
            margin: [0, 5, 0, 0],
            table: {
                widths: ['*', 80, 60],
                body: [[
                    {
                        text: 'Paid amount',
                        alignment: 'right'
                    },
                    {
                        text: getPaidAmount(invoice),
                        alignment: 'center',
                        bold: true
                    },
                    {
                        text: getPaymentStatus(invoice),
                        alignment: 'center',
                        bold: true
                    }
                ]]
            },
            layout: {
                hLineWidth: function () {
                    return 1;
                },
                vLineWidth: function () {
                    return 0;
                }
            }
        }
    }

    const getLabelValue = (label, value) => {
        return {
            columns: [
                {
                    width: 62,
                    text: label,
                    bold: true
                },
                {
                    width: '*',
                    text: value
                }
            ]
        }
    }

    const getBillNumber = (invoice) => {
        if (! invoice.bill_number) return '';
        return invoice.bill_number;
    }

    const getBillDate = (invoice) => {
        if (! invoice.date) return '';
        return formatToDdMmYy(invoice.date.split(' ')[0], false);
    }

    const getPatientName = (invoice) => {
        if (! invoice.contact) return '';
        if (! invoice.contact.name) return '';
        return invoice.contact.name.toUpperCase();
    }

    const getPatientAge = (invoice) => {
        if (! invoice.contact) return '';
        if (! invoice.contact.birthday) return '';
        return calculateAge(invoice.contact.birthday, invoice.date);
    }

    const getPatientGender = (invoice) => {
        if (! invoice.contact) return '';
        return capitalize(invoice.contact.gender);
    }

    const getPatientId = (invoice) => {
        if (invoice.contact_profile && invoice.contact_profile.patient_id) return invoice.contact_profile.patient_id;
        if (! invoice.contact) return '';
        return invoice.contact.id;
    }

    const getPatientMobile = (invoice) => {
        if (! invoice.contact) return '';
        if (! invoice.contact.profile_items) return '';
        const field = invoice.contact.profile_items.find(function (info) {
            return info.field === 'mobile_no';
        });
        if (! field) return '';
        if (! field.value) return '';
        return field.value;
    }

    const getDoctorName = (invoice) => {
        if (invoice.investigation_consultant) return invoice.investigation_consultant;
        if (invoice.consultant) return invoice.consultant;
        const serviceResource = getServiceResource(invoice);
        if (! serviceResource) return '';
        if (! serviceResource.contact_profile_id) return '';
        if (! serviceResource.name) return '';
        return serviceResource.name;
    }

    const getPaidAmount = (invoice) => {
        if (! invoice.paid_amount) return '0';
        return invoice.paid_amount;
    }

    const getPaymentStatus = (invoice) => {
        if (! invoice.paid_amount) return 'DUE';
        return 'PAID';
    }

    const capitalize = (text) => {
        if (! text) return '';
        if (typeof text !== 'string') return '';
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    return {
        exportToPDF
    }
}

export default opdPrescriptionPdf;
